import { Text, TextColor, TextSize, TextWeight, Tooltip, TooltipTrigger } from '@vizit/atoms';
import { Skeleton } from '@vizit/atoms';
import { cx } from '@vizit/theme';
import * as React from 'react';
import { Focusable } from 'react-aria-components';

interface ProductHeaderInfoItemProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The label of the product header info item
   * @required
   * @type {string}
   */
  label: string;
  /**
   * The value of the product header info item
   * @optional
   * @type {string}
   * @default '-'
   */
  value?: string;
  /**
   * Whether the product header info item is loading
   * @required
   * @type {boolean}
   */
  isLoading: boolean;
  /**
   * Optional rich content shown in a tooltip when hovering or focusing the value.
   * Not rendered while loading.
   * @optional
   * @type {React.ReactNode}
   */
  tooltip?: React.ReactNode;
  /**
   * Optional icon shown before the value. Not rendered while loading.
   * @optional
   * @type {React.ReactNode}
   */
  valueIcon?: React.ReactNode;
}

const ProductHeaderInfoItem = React.memo(
  ({ label, value = '-', isLoading, tooltip, valueIcon, className, ...other }: ProductHeaderInfoItemProps) => {
    const hasTooltip = !isLoading && !!tooltip;
    const valueText = (
      <Text
        showEllipsis
        elementType="span"
        size={TextSize.Xs}
        color={TextColor.Primary}
        weight={TextWeight.Regular}
        // A faint dotted underline hints that the value has a tooltip
        className={cx(hasTooltip && 'underline decoration-dotted decoration-gray-utility-300 underline-offset-2')}
      >
        {value}
      </Text>
    );
    const valueContent = valueIcon ? (
      <span className="flex min-w-0 items-center gap-3">
        {valueIcon}
        {valueText}
      </span>
    ) : (
      valueText
    );

    return (
      <div className={cx('flex flex-col', className)} aria-label={`${label} information`} {...other}>
        <Text
          showEllipsis
          elementType="span"
          size={TextSize.Xs}
          weight={TextWeight.Semibold}
          color={TextColor.Tertiary}
        >
          {label}
        </Text>
        {isLoading ? (
          <Skeleton className="h-12 w-1/2" variant="square" aria-label={`Loading ${label}`} />
        ) : tooltip ? (
          <TooltipTrigger delay={200}>
            {/* The value is a plain span; Focusable lets it drive the tooltip and keeps it keyboard-reachable */}
            <Focusable>
              <span
                // biome-ignore lint/a11y/noNoninteractiveTabindex: the value must be keyboard-reachable to open its tooltip
                tabIndex={0}
                className="flex min-w-0 cursor-default rounded-xs outline-none focus-visible:ring-2 focus-visible:ring-gray-modern-500"
              >
                {valueContent}
              </span>
            </Focusable>
            <Tooltip placement="bottom">{tooltip}</Tooltip>
          </TooltipTrigger>
        ) : (
          valueContent
        )}
      </div>
    );
  },
);

export { ProductHeaderInfoItem };
